import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { boostService } from "../../features/boost/service";
import type { Campaign, Wallet } from "../../features/boost/types";
import { parseApiError } from "../../lib/http/apiErrors";

function fmtCredits(n: number) { return `${n.toLocaleString("en-IN")} credits`; }

function StatusBadge({ status }: { status: Campaign["status"] }) {
  const map: Record<Campaign["status"], { label: string; cls: string }> = {
    ACTIVE:           { label: "Active",    cls: "bg-emerald-100 text-emerald-700" },
    PAUSED:           { label: "Paused",    cls: "bg-amber-100 text-amber-700" },
    BUDGET_EXHAUSTED: { label: "Budget out", cls: "bg-orange-100 text-orange-700" },
    EXPIRED:          { label: "Expired",   cls: "bg-gray-100 text-gray-500" },
    CANCELLED:        { label: "Cancelled", cls: "bg-red-100 text-red-500" },
  };
  const { label, cls } = map[status] ?? { label: status, cls: "bg-gray-100 text-gray-500" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

function pct(spent: number, total: number) {
  if (!total) return 0;
  return Math.min(100, Math.round((spent / total) * 100));
}

export default function BoostCampaignsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justActivated = params.get("activated") === "1";
  const topupCredits = parseInt(params.get("topup_credits") ?? "0", 10);
  const justToppedUp = topupCredits > 0;
  const stripeSessionId = params.get("stripe_session_id") ?? "";
  const cfOrderId = params.get("cf_order_id") ?? "";

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [autoActivated, setAutoActivated] = useState(false);
  const [pollingComplete, setPollingComplete] = useState(false);
  const [pollTrigger, setPollTrigger] = useState(0);
  const [fallbackCreating, setFallbackCreating] = useState(false);
  const [fallbackError, setFallbackError] = useState("");
  const campaignCountAtMountRef = useRef<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        boostService.getCampaigns(),
        boostService.getWallet(),
      ]);
      setCampaigns(c);
      setWallet(w);
      if (campaignCountAtMountRef.current === null) {
        campaignCountAtMountRef.current = c.length;
        // Campaign already created before page loaded (e.g. manual retry or fast webhook)
        if (justToppedUp && c.some(camp => camp.status === "ACTIVE")) {
          setAutoActivated(true);
        }
      }
    } catch {
      setError("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Cashfree redirect settlement. Cashfree returns to us with ?cf_order_id=...
  // (no client-side confirm like Razorpay, no session like Stripe). Actively
  // pull the top-up status so the wallet is credited and the pending campaign
  // is created even if the provider webhook is delayed or missed (common in
  // local/dev). Idempotent on the backend — safe to retry.
  useEffect(() => {
    if (!cfOrderId || autoActivated) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const settle = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const res = await boostService.topupStatus(cfOrderId, "cashfree");
        if (!cancelled && res.settled) {
          // Wallet credited + campaign creation triggered; refresh both.
          const [c, w] = await Promise.all([
            boostService.getCampaigns(),
            boostService.getWallet(),
          ]);
          if (cancelled) return;
          setCampaigns(c);
          setWallet(w);
          if (c.some(camp => camp.status === "ACTIVE")) {
            setAutoActivated(true);
            return;
          }
        }
      } catch {
        // transient — retry below
      }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(() => void settle(), 3000);
      } else if (!cancelled) {
        setPollingComplete(true);
      }
    };

    const t = setTimeout(() => void settle(), 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cfOrderId, autoActivated]);

  // Poll for campaign created by the Stripe webhook after redirect.
  useEffect(() => {
    if (!justToppedUp || autoActivated) return;
    let cancelled = false;
    let pollCount = 0;
    const maxPolls = 10;

    const poll = async () => {
      if (cancelled) return;
      pollCount++;
      try {
        const [c, w] = await Promise.all([boostService.getCampaigns(), boostService.getWallet()]);
        const baseline = campaignCountAtMountRef.current ?? 0;
        if (c.length > baseline) {
          setCampaigns(c);
          setWallet(w);
          setAutoActivated(true);
          return;
        }
      } catch {}
      if (pollCount < maxPolls && !cancelled) {
        setTimeout(() => void poll(), 3000);
      } else if (!cancelled) {
        // Webhook didn't fire in time (common in local/dev). Auto-trigger process-pending.
        if (stripeSessionId) {
          setFallbackCreating(true);
          try {
            const result = await boostService.processPendingCampaign(stripeSessionId);
            if (result.ok) {
              const [c, w] = await Promise.all([boostService.getCampaigns(), boostService.getWallet()]);
              setCampaigns(c); setWallet(w); setAutoActivated(true);
            } else {
              setFallbackError(result.reason ?? "Campaign setup pending — check back shortly.");
              setPollingComplete(true);
            }
          } catch {
            setFallbackError("Campaign will be activated shortly. Refresh to check.");
            setPollingComplete(true);
          } finally {
            setFallbackCreating(false);
          }
        } else {
          setPollingComplete(true);
        }
      }
    };

    const t = setTimeout(() => void poll(), 2000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [justToppedUp, autoActivated, pollTrigger, stripeSessionId]);


  async function handleAction(id: string, action: "pause" | "resume" | "cancel") {
    setActing(id);
    try {
      const updated = await boostService.patchCampaign(id, action);
      setCampaigns(prev => prev.map(c => c.id === id ? updated : c));
    } catch (e: unknown) {
      setError(parseApiError(e, "Action failed").message);
    } finally {
      setActing(null);
    }
  }

  const active = campaigns.filter(c => c.status === "ACTIVE").length;
  const totalSpent = campaigns.reduce((s, c) => s + (c.spent_credits ?? 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/inventory/my")}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium">
              ← Inventory
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900">Boost Campaigns</h1>
              <p className="text-xs text-gray-500">{active} active</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wallet && (
              <div className="text-right">
                <p className="text-[10px] text-gray-400">Wallet</p>
                <p className="text-sm font-bold text-gray-900">{fmtCredits(wallet.balance_credits)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Activating campaign after Stripe top-up */}
        {justToppedUp && !autoActivated && !pollingComplete && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">
                {fallbackCreating ? "Creating your campaign…" : "Activating your boost campaign…"}
              </p>
              <p className="text-xs text-blue-600 mt-0.5">Payment received. Campaign launches automatically — this takes a few seconds.</p>
            </div>
          </div>
        )}

        {/* Polling timed out — show error or retry */}
        {justToppedUp && !autoActivated && pollingComplete && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-emerald-800">Payment received — {fmtCredits(topupCredits)} in your wallet</p>
            <p className="text-xs text-emerald-700 mt-1">
              {fallbackError || "Campaign is being set up. Check back in a moment."}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => { setPollingComplete(false); setFallbackError(""); setPollTrigger(t => t + 1); void load(); }}
                className="px-3 py-1.5 text-xs font-semibold text-emerald-800 bg-emerald-100 rounded-lg border border-emerald-300 hover:bg-emerald-200 transition-colors"
              >
                Check again
              </button>
            </div>
          </div>
        )}

        {/* Auto-activated success */}
        {autoActivated && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-emerald-600 text-lg">⚡</span>
            <div>
              <p className="text-sm font-semibold text-emerald-800">Campaign activated!</p>
              <p className="text-xs text-emerald-700 mt-0.5">Payment received and boost is live. Your deal appears at the top of search results within 60 seconds.</p>
            </div>
          </div>
        )}

        {/* Campaign activated — either via direct launch or after Stripe topup */}
        {(justActivated || autoActivated) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-emerald-600 text-lg">⚡</span>
            <div>
              <p className="text-sm font-semibold text-emerald-800">Campaign activated!</p>
              <p className="text-xs text-emerald-700 mt-0.5">Payment received and boost is live. Your deal appears at the top of search results within 60 seconds.</p>
            </div>
          </div>
        )}

        {/* Stats row */}
        {campaigns.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{active}</p>
              <p className="text-[11px] text-gray-500">Active</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">
                {campaigns.reduce((s, c) => s + (c.impression_count ?? 0), 0).toLocaleString("en-IN")}
              </p>
              <p className="text-[11px] text-gray-500">Impressions</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">
                {campaigns.reduce((s, c) => s + (c.click_count ?? 0), 0).toLocaleString("en-IN")}
              </p>
              <p className="text-[11px] text-gray-500">Clicks</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{fmtCredits(totalSpent)}</p>
              <p className="text-[11px] text-gray-500">Spent</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading campaigns…</div>
        ) : campaigns.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-xl border border-dashed border-gray-200">
            <div className="text-4xl mb-3">⚡</div>
            <p className="font-semibold text-gray-900">No campaigns yet</p>
            <p className="text-sm text-gray-500 mt-1">Go to your inventory and click Boost on any live deal.</p>
            <button onClick={() => navigate("/inventory/my")}
              className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
              Go to Inventory
            </button>
          </div>
        ) : (
          campaigns.map(c => {
            const spentPct = pct(c.spent_credits ?? 0, c.total_budget_credits ?? 1);
            const dailyPct = pct(c.daily_spent_credits ?? 0, c.daily_budget_credits ?? 1);
            const isActing = acting === c.id;

            return (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4">
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {c.deal_title ?? c.deal_id}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={c.status} />
                        <span className="text-[11px] text-gray-400">
                          {c.surfaces.join(" + ")}
                        </span>
                        {c.deal_categories.length > 0 && (
                          <span className="text-[11px] text-gray-400">· {c.deal_categories.slice(0, 3).join(", ")}{c.deal_categories.length > 3 ? "…" : ""}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-400">Daily budget</p>
                      <p className="text-sm font-bold text-gray-900">{fmtCredits(c.daily_budget_credits ?? 0)}</p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div>
                      <p className="text-[11px] text-gray-400">Impressions</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {(c.impression_count ?? 0).toLocaleString("en-IN")}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-400">Clicks</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {(c.click_count ?? 0).toLocaleString("en-IN")}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-400">CTR</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {c.impression_count ? `${((c.click_count ?? 0) / c.impression_count * 100).toFixed(1)}%` : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-400">Spent</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {fmtCredits(c.spent_credits ?? 0)}
                        <span className="text-[11px] text-gray-400"> / {fmtCredits(c.total_budget_credits ?? 0)}</span>
                      </p>
                    </div>
                  </div>

                  {/* Progress bar — total budget */}
                  <div className="mb-3">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${spentPct}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>{spentPct}% spent</span>
                      <span>
                        {new Date(c.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                  </div>

                  {/* Daily bar */}
                  {c.status === "ACTIVE" && (
                    <div className="mb-3">
                      <p className="text-[10px] text-gray-400 mb-0.5">Today's spend</p>
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 rounded-full transition-all"
                          style={{ width: `${dailyPct}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    {c.status === "ACTIVE" && (
                      <button onClick={() => void handleAction(c.id, "pause")} disabled={isActing}
                        className="flex-1 py-1.5 text-xs font-semibold border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-60 transition-colors">
                        {isActing ? "…" : "Pause"}
                      </button>
                    )}
                    {c.status === "PAUSED" && (
                      <button onClick={() => void handleAction(c.id, "resume")} disabled={isActing}
                        className="flex-1 py-1.5 text-xs font-semibold border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-60 transition-colors">
                        {isActing ? "…" : "Resume"}
                      </button>
                    )}
                    {["ACTIVE", "PAUSED", "BUDGET_EXHAUSTED"].includes(c.status) && (
                      <button onClick={() => void handleAction(c.id, "cancel")} disabled={isActing}
                        className="px-3 py-1.5 text-xs font-semibold border border-red-200 text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-60 transition-colors">
                        {isActing ? "…" : "Cancel"}
                      </button>
                    )}
                    <button onClick={() => navigate(`/boost/campaigns/${c.id}/analytics`)}
                      className="px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 hover:border-blue-300 rounded-lg transition-colors">
                      Analytics
                    </button>
                  </div>
                </div>

              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
